import { QtySelector } from '@commerce-cart-webcomponents/ui-composite';
import { InstalmentDetails } from '@commerce-payment-webcomponents/ui';
import { DerivedAvailabilityStatusInternal, IProduct, ProductType } from '@commerce-product-webcomponents/sdk';
import type { BundlePropType } from '@commerce-product-webcomponents/ui';
import {
  AddToWishlistContainer,
  BasicDetailsComponent,
  CardType,
  ConditionalRetailPriceRangeComponent,
  DetailsType,
  Dispositionlabels,
  DispositionStockStatus,
  DropdownButton,
  DropdownButtonTypeEnum,
  IdAndSizeType,
  ImageComponent,
  ImageType,
  includesDerivedAvailabilityStockStatus,
  PriceComponent,
  PriceType,
  ProductBundleStyleType,
  ProductIdAndSizeComponent,
  ProductLink,
  ProductQuantityComponent,
  ProductQuantityType,
  ProductsBundle,
  PVBVComponent,
  PVBVType,
  StockStatusComponent,
  StockStatusComponentGroupKey,
  StockStatusComponentType,
} from '@commerce-product-webcomponents/ui';
import { PromotionDetails } from '@commerce-promotion-webcomponents/ui';
import {
  AlertMessage,
  Breakpoints,
  ButtonSize,
  Checkbox,
  FAIcon,
  IconAlignment,
  IconStyle,
  SkeletonFrame,
  Theme,
  useBreakpoint,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { FC, useEffect, useState } from 'react';
import { keyCheck } from '../../../assets';
import { CartDrawer } from '../../Molecules';
import styles from './CartCardComponentV2.module.scss';
import { isPartialBackOrderCondition } from './cartCardUtils';
import TagsContainer from './TagsContainer';
import { CartCardComponentV2PropsTypes, IData, UpdateAvailabilityStatusAndDate } from './types/propsTypes';

const CartCardComponentV2: FC<CartCardComponentV2PropsTypes> = (props) => {
  const {
    data,
    onRemoveProduct,
    onModifyBundleButtonClick,
    forceMobileView = false,
    showPropertiesCollection: {
      showCartInventoryUpdate = false,
      showPromotionLoader = false,
      showItemId = true,
      showQtyStepper = true,
      showPVBV = true,
      showStockStatus = true,
      showHeartIcon = false,
      showCancelIcon = false,
      showCheckBox = false,
      showQuantityLabel = false,
      showRemoveIcon = false,
      showSubscriptionCard = false,
      showModifyBundleButton = false,
      showInstallment = false,
      hasInfiniteShipment = false,
      showPriceLabel = true,
      disableVariantDropdown = false,
      showDispositionLinkCTA = true,
      showRetailPrice = false,
      showSubscriptionTag = false,
      showPromotionTag = false,
      showApointTag = false,
      showCompensatedSOPTag = false,
      showPromotionBenefitTag = false,
      promotionBenefitTagText = '',
    } = {},
    checkboxProperties,
    subscriptionQtyStepperComp,
    subscriptionVariantActions,
    updateQtyCustomHandler,
    minValueForStepper,
  } = props;

  const {
    productId,
    entryId,
    brandName,
    productName,
    link,
    alt,
    productPrice,
    currency,
    variantName,
    variantGroup,
    disabled,
    productSize,
    strikethroughPrice,
    itemDisposition,
    bundle,
    firedPromotions,
    potentialPromotions,
    pointValue = '',
    businessVolume = '',
    showSubText,
    dateSubText,
    showPrefix,
    derivedAvailabilityStatus = '',
    firstAvailableFutureDate = '',
    isSingleLineBasicDetail,
    productQuantity,
    minOrder,
    maxOrder,
    subscriptionId,
    subscriptionProducts,
    productType,
    restrictFromPdpDisplay,
    showZeroAsFree,
    productRetailPrice,
    handleDropdown,
  } = data;

  const { initialCheckBoxVal = false, isCheckBoxDisabled = false, onCheckboxClick } = checkboxProperties || {};

  const { isAnonymous } = useSession();

  const getCTAButtonStatus = (productStatus = '') =>
    productStatus
      ? includesDerivedAvailabilityStockStatus(productStatus, StockStatusComponentGroupKey.CTA_DISABLED_STATUSES)
      : false;
  const showDispositionStatus = derivedAvailabilityStatus !== DerivedAvailabilityStatusInternal.Naw;

  const [cartButtonStatus, setCartButtonStatus] = useState<boolean>(getCTAButtonStatus(derivedAvailabilityStatus));

  const handleDropdownEvent = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (handleDropdown && typeof handleDropdown === 'function') {
      handleDropdown(e);
      return;
    }
    setOpenDrawer(true);
  };

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [displayInventoryUpdate, setDisplayInventoryUpdate] = useState(true);
  const [updatedStatusAndDate, setUpdatedStatusAndDate] = useState<UpdateAvailabilityStatusAndDate>({
    derivedAvailabilityStatus,
    firstAvailableFutureDate,
  });
  const isStockStatusAvailable = !!derivedAvailabilityStatus;
  const t = useTranslation();
  const viewPort = useBreakpoint();
  const checkoutInventoryLabel = t('product.checkoutInventory.text');
  const checkoutInventoryDismissLabel = t('product.checkoutInventory.dismiss');
  const derivedShowLink = !restrictFromPdpDisplay && !showSubscriptionCard ? true : false;
  const productLinkProps = {
    href: '',
    showLink: derivedShowLink,
    productId,
    productName,
  };

  const checkboxClickHandler = (e: React.MouseEvent) => {
    onCheckboxClick?.(e, productId, initialCheckBoxVal);
  };

  const handleKeyDownRemove = (e: React.KeyboardEvent) => {
    if (keyCheck(e.code)) {
      onRemoveProduct(productId);
    }
  };

  const updateStockStatusDetails = () => {
    const { derivedAvailabilityStatus: status, farthestDateAvailable } = isPartialBackOrderCondition(
      bundle,
      firstAvailableFutureDate,
      derivedAvailabilityStatus,
    );
    setUpdatedStatusAndDate({
      derivedAvailabilityStatus: status,
      firstAvailableFutureDate: farthestDateAvailable,
    });
  };

  useEffect(() => {
    setCartButtonStatus(getCTAButtonStatus(derivedAvailabilityStatus));
    updateStockStatusDetails();
  }, [derivedAvailabilityStatus, itemDisposition]);

  const subscriptionTagText = hasInfiniteShipment
    ? t('product.cartCardComponent.subscriptionOrder').replace(/{{elementSequence}}/g, String(minOrder))
    : t('product.cartCardComponent.subscription')
        .replace(/{{minOrder}}/g, String(minOrder))
        .replace(/{{maxOrder}}/g, String(maxOrder));

  return (
    <div
      className={`${styles['cart-card-container']} ${
        forceMobileView && styles['product-info-container-force-mobile-view']
      }`}
    >
      {openDrawer && (
        <CartDrawer
          link={link}
          alt={alt}
          brandName={brandName}
          productName={productName}
          productPrice={productPrice}
          currency={currency}
          productId={productId}
          productSize={''}
          cartBaseurl=""
          onClose={() => {
            setOpenDrawer(false);
          }}
          entryId={entryId as number}
          derivedAvailabilityStockStatus={updatedStatusAndDate.derivedAvailabilityStatus}
          subscriptionVariantActions={subscriptionVariantActions}
          variantGroup={variantGroup}
          showPriceLabel={showPriceLabel}
          showZeroAsFree={showZeroAsFree}
        />
      )}
      <div className={`${styles['product-info-container']}`}>
        <div className={styles['product-and-tag-container']} data-testid="product-and-tag-container">
          <div className={styles['tag-container']}>
            <TagsContainer
              subscriptionTagText={subscriptionTagText}
              showApointTag={showApointTag}
              showCompensatedSOPTag={showCompensatedSOPTag}
              showPromotionTag={showPromotionTag}
              showSubscriptionTag={showSubscriptionTag}
              showPromotionBenefitTag={showPromotionBenefitTag}
              promotionBenefitTagText={promotionBenefitTagText}
            />
          </div>
          <div className={styles['product-container']} data-testid="product-container">
            <div className={styles['left-panel']}>
              {showCheckBox && (
                <div className={styles['cart-product-checkbox-container']}>
                  <Checkbox
                    text={''}
                    checked={initialCheckBoxVal}
                    onClick={checkboxClickHandler}
                    disabled={isCheckBoxDisabled}
                    data-testid="cart-card-checkbox-control"
                  />
                </div>
              )}

              <div className={styles['card-image']}>
                <ProductLink className={styles['image-link']} {...productLinkProps}>
                  <span data-testid="card-image-button" aria-label={alt}>
                    <ImageComponent link={link} alt={alt} imagetype={ImageType.CART_CARD} />
                  </span>
                </ProductLink>
              </div>
            </div>
            <div className={styles['container']}>
              <div className={styles['product-info']} data-testid="product-info">
                <div className={styles['product-detail']} data-testid="product-detail">
                  <ProductLink {...productLinkProps}>
                    <BasicDetailsComponent
                      brandName={brandName}
                      productName={productName}
                      isSingleLineBasicDetail={isSingleLineBasicDetail}
                      detailsType={
                        forceMobileView ? DetailsType.CART_CARD_MOBILE_VARIANT : DetailsType.CART_CARD_VARIANT
                      }
                    />
                  </ProductLink>
                  {subscriptionId ? (
                    <ProductIdAndSizeComponent
                      productId={`${t('product.cartCardComponent.subscriptionId')} ${subscriptionId}`}
                      idSizeType={
                        forceMobileView ? IdAndSizeType.ID_SIZE_CART_MOBILE_VARIANT : IdAndSizeType.IDSIZECART
                      }
                      productSize={''}
                      showProductId={true}
                    />
                  ) : (
                    <ProductIdAndSizeComponent
                      productId={showItemId ? `${t('product.common.item')} ${productId}` : ''}
                      productSize={productSize}
                      idSizeType={
                        forceMobileView ? IdAndSizeType.ID_SIZE_CART_MOBILE_VARIANT : IdAndSizeType.IDSIZECART
                      }
                      showProductId={true}
                    />
                  )}
                  {showInstallment && !isAnonymous() && (
                    <div className={styles['left-panel-installments']}>
                      <InstalmentDetails productId={productId} />
                    </div>
                  )}
                </div>

                <div className={styles['amount-container']}>
                  <div className={styles['amount']}>
                    {showPriceLabel && (
                      <PriceComponent
                        productPrice={productPrice}
                        currency={currency}
                        strikethroughPrice={strikethroughPrice}
                        priceType={forceMobileView ? PriceType.CART_CARD_MOBILE_VARIANT : PriceType.CART_CARD_VARIANT}
                        showZeroAsFree={showZeroAsFree}
                      />
                    )}
                    {showRetailPrice && (
                      <ConditionalRetailPriceRangeComponent
                        isBundle={false}
                        currency={currency}
                        productPrice={productRetailPrice as number}
                        condionalPriceType={
                          forceMobileView ? PriceType.CART_CARD_MOBILE_VARIANT : PriceType.CART_CARD_VARIANT
                        }
                        isRetailPrice={true}
                      />
                    )}
                    {showPVBV && (pointValue || businessVolume) && (
                      <div className={styles['pvbv-container']}>
                        <PVBVComponent
                          pointValue={pointValue}
                          businessVolume={businessVolume}
                          pvbvType={forceMobileView ? PVBVType.PVBVCARTMOBILE : PVBVType.PVBVCART}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {!cartButtonStatus && isStockStatusAvailable && showStockStatus && (
                  <div className={styles['stock-status']} data-testid="stock-status">
                    <StockStatusComponent
                      {...updatedStatusAndDate}
                      showSubText={showSubText}
                      dateSubText={dateSubText}
                      stockStatusType={
                        forceMobileView
                          ? StockStatusComponentType.STOCK_STATUS_CART_ENTRY_MOBILE
                          : StockStatusComponentType.STOCK_STATUS_CART_ENTRY
                      }
                      showPrefix={showPrefix}
                    />
                  </div>
                )}
              </div>
              <div className={styles['button-group']} data-testid="button-group">
                {!cartButtonStatus && variantName && productType === ProductType.Variant && (
                  <DropdownButton
                    label={variantName}
                    disabled={disableVariantDropdown}
                    dropdownEventHandler={handleDropdownEvent}
                    className={styles['cart-variant-btn']}
                    dropdownType={
                      forceMobileView
                        ? DropdownButtonTypeEnum.CART_CARD_MOBILE_VARIANT
                        : DropdownButtonTypeEnum.CART_CARD_VARIANT
                    }
                  />
                )}

                {showQtyStepper && (
                  <div>
                    {subscriptionQtyStepperComp ? (
                      subscriptionQtyStepperComp
                    ) : (
                      <QtySelector
                        maxCount={100}
                        min={minValueForStepper}
                        disabled={cartButtonStatus || disabled}
                        showRightIcon={false}
                        entryObj={{
                          productId,
                          entryId: entryId as number,
                        }}
                        size={ButtonSize.MEDIUM}
                        {...(updateQtyCustomHandler && {
                          updateQuantityCustomHandler: updateQtyCustomHandler,
                        })}
                      />
                    )}
                  </div>
                )}
                {!showQtyStepper && showQuantityLabel && (
                  <div className={styles['quantity-label-padding']}>
                    <ProductQuantityComponent
                      productQuantity={productQuantity}
                      productQuantityType={ProductQuantityType.CART_CARD_VARIANT}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className={styles['right-panel']} data-testid="right-panel">
              <div>
                {showCancelIcon ? (
                  <button
                    className={styles['cancel-container']}
                    onClick={() => onRemoveProduct(productId)}
                    onKeyDown={handleKeyDownRemove}
                    data-testid="cart-card-cancel-cross-icon"
                  >
                    <FAIcon iconName={'xmark'} iconStyle={IconStyle.REGULAR} />
                  </button>
                ) : (
                  <>
                    {showHeartIcon && (
                      <AddToWishlistContainer
                        isBundle={!!bundle}
                        isPLPCard={true}
                        isPDP={false}
                        withText={false}
                        productData={{ ...data } as unknown as IProduct}
                      />
                    )}
                  </>
                )}
              </div>
              {showRemoveIcon && (
                <FAIcon
                  iconName="trash-can"
                  onClick={() => onRemoveProduct(productId)}
                  className={styles['delete-container']}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles['bundle-and-alerts-container']}>
          <div className={styles['bundle-container']}>
            {((bundle?.length as number) > 0 || showSubscriptionCard) && (
              <ProductsBundle
                onModifyBundleButtonClick={onModifyBundleButtonClick}
                showModifyBundleButton={showModifyBundleButton}
                bundle={bundle as BundlePropType[]}
                showSubscriptionDetails={showSubscriptionCard}
                subscriptionProducts={subscriptionProducts}
                cardType={CardType.CART_CARD}
                productBundlStyleType={showSubscriptionCard ? ProductBundleStyleType.MANUAL_SOP_PRODUCT_BUNDLE : ''}
                restrictFromPdpDisplay={restrictFromPdpDisplay}
                showZeroAsFree={showZeroAsFree}
              />
            )}
          </div>
          {showPromotionLoader ? (
            <div>
              <SkeletonFrame
                heightValue={viewPort === Breakpoints.XS ? '36' : '40'}
                borderRadiusValue="8"
                isRoundCorner={true}
              />
            </div>
          ) : (
            (!!potentialPromotions?.length || !!firedPromotions?.length) && (
              <div>
                <PromotionDetails firedPromotions={firedPromotions} potentialPromotions={potentialPromotions} />
              </div>
            )
          )}
          {showCartInventoryUpdate && displayInventoryUpdate && (
            <div data-testid="cart-inventory-status">
              <AlertMessage
                theme={Theme.ACTION}
                className={styles['cart-inventory-status']}
                icon="exclamation-circle"
                iconStyle={IconStyle.REGULAR}
                iconColor={Theme.PURPLE}
                content={{
                  heading: checkoutInventoryLabel,
                }}
                textLink={{
                  text: checkoutInventoryDismissLabel,
                  onClick: () => setDisplayInventoryUpdate(false),
                }}
                iconAlignment={IconAlignment.CENTER}
                textColor="#38539a"
                bgColor="#f1f2ff"
              />
            </div>
          )}
          {cartButtonStatus && itemDisposition && showDispositionStatus && (
            <div data-testid="cart-disposition-test-id">
              <DispositionStockStatus
                dispositionlabels={Dispositionlabels}
                variantName={variantName}
                showLinkCTA={showDispositionLinkCTA}
                dispositionType={itemDisposition}
                onDispositionLabelClick={() => (variantName ? setOpenDrawer(true) : onRemoveProduct(productId))}
                dispositionValue={variantName ? Dispositionlabels.CHANGE_VARIANT : Dispositionlabels.REMOVE}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartCardComponentV2;
export type { BundlePropType, CartCardComponentV2PropsTypes, IData };
